import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './builder/TokenAwareTextField';
import {
  useCampaignMutations,
  useFetchAiQuestions,
  useFetchCampaigns,
  useReportSectionConfig,
} from '../../../hooks/campaign-hooks';
import { Campaign, CampaignReportSection, ReportSectionSource } from '../../../support/campaigns';
import { REPORT_TOKENS } from '../../Campaigns/guided/modules/report/tokens';
import { ReportCanvas } from './ReportCanvas';
import { SectionGridCanvas } from './SectionGridCanvas';
import {
  BuilderRoot,
  Card,
  GhostButton,
  PrimaryButton,
  SectionHeader,
  StatusPill,
  builderStyles,
} from './builder/primitives';
import { TokenTextField } from './builder/TokenTextField';

/**
 * Management > Campaigns > Report Builder.
 *
 * The data-driven, author-editable replacement for the frontier runner's hard-coded report constants
 * (`REPORT_SECTION_QUESTIONS` / `DATA_SECTION` / `DEFAULT_HIDDEN_SECTIONS`). Every campaign declares
 * its own report template as `CampaignReportSection` rows and the runner consumes them (see
 * `useReportSectionConfig` → `RunnerDataContext.reportSections`), so a new/unrelated campaign gets its
 * own report with no code change — this surface is where those rows are edited.
 *
 * A section's `section_id` is the report engine's string section id; `ai_question` binds the stored
 * prompt that writes it (null = a template/deterministic section, no AI text); `is_data_section`
 * marks the section the workshop's captured data drafts into; `hidden_by_default` seeds it
 * switched-off in the report editor's section toggles. Writes are full-record PUTs (Go runtime),
 * reorder swaps `order` with the neighbour.
 *
 * The open campaign rides the `?rc=<id>` query param so a saved link survives a reload (distinct from
 * the Builder's `?c=`). Write controls degrade to read-only when `canWrite` is false.
 */

const SOURCE_OPTIONS: Array<{ value: ReportSectionSource; label: string }> = [
  { value: 'template', label: 'Template prose' },
  { value: 'generated', label: 'Generated at report time' },
];

// The report engine's standard section ids (mirror of the bundled REPORT_TEMPLATE in the frontier
// runner). Offered as freeSolo suggestions so the standard ids are one click and a typo is visible;
// a new/custom section id is still allowed (this stays authorable — it is the id others point at).
const KNOWN_SECTION_IDS = [
  'executive-summary',
  'key-workshop-findings',
  'post-workshop-action',
  'post-workshop-recommendations',
  'recommended-immediate-next',
  'resources-tool-directory',
  'appendix-3-unified-asset',
  'appendix-4-live-protect',
  'appendix-5-ai-defense',
  'appendix-6-references',
];

/**
 * Compact, read-only reminder of the legacy per-delivery `[Bracketed]` substitution tokens an author
 * can drop into any authored text (prose, section titles, figure captions). Sourced directly from the
 * runner's `REPORT_TOKENS` (itself derived from `AUTHOR_TOKEN_DEFS`) so it can never drift from what
 * actually resolves. The normalized `{{group.key}}` references — the full campaign attribute catalog —
 * are inserted from the picker attached to each prose field; these brackets remain valid too.
 */
function TokenLegend(): JSX.Element {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      Insert per-delivery values: {REPORT_TOKENS.map((t) => t.token).join('  ')}
    </Typography>
  );
}

/**
 * A section's authored body, one block at a time. Structurally the runner's `TemplateBlock`
 * (`Campaigns/guided/modules/report/types.ts`), redeclared locally so this admin surface stays
 * decoupled from the vendored frontier subtree — the shape is a stable data contract (the runner reads
 * `blocks` back through `resolveReportTemplate`), not an import.
 */
type ProseBlockKind = 'p' | 'bullet' | 'number' | 'h1' | 'h2' | 'h3' | 'h4' | 'table';
interface ProseBlock {
  kind: ProseBlockKind;
  /** A bold label opening the paragraph, where the section has one. */
  lead?: string;
  text?: string;
  /** Only for `kind: 'table'` — rows of cell strings. */
  rows?: string[][];
}

const BLOCK_KIND_OPTIONS: Array<{ value: ProseBlockKind; label: string }> = [
  { value: 'p', label: 'Paragraph' },
  { value: 'bullet', label: 'Bulleted item' },
  { value: 'number', label: 'Numbered item' },
  { value: 'h2', label: 'Heading' },
  { value: 'h3', label: 'Subheading' },
  { value: 'h4', label: 'Minor heading' },
  { value: 'h1', label: 'Title' },
  { value: 'table', label: 'Table' },
];

/** Coerce a section's loosely-typed `blocks: unknown[]` into the prose-block shape for editing. */
function asProseBlocks(blocks: unknown[] | undefined): ProseBlock[] {
  if (!Array.isArray(blocks)) return [];
  return blocks.map((b) => {
    const o = (b ?? {}) as Record<string, unknown>;
    const kind = (BLOCK_KIND_OPTIONS.some((k) => k.value === o.kind) ? o.kind : 'p') as ProseBlockKind;
    const block: ProseBlock = { kind };
    if (typeof o.lead === 'string') block.lead = o.lead;
    if (typeof o.text === 'string') block.text = o.text;
    if (Array.isArray(o.rows)) {
      block.rows = (o.rows as unknown[]).map((r) =>
        Array.isArray(r) ? r.map((c) => (typeof c === 'string' ? c : String(c ?? ''))) : [],
      );
    }
    return block;
  });
}

/**
 * Friendly editor for one section's authored prose (`CampaignReportSection.blocks`) — a reorderable
 * list of typed blocks (paragraphs, bullets, headings, a table), so the report body is authored here
 * rather than living only in the runner's bundled template. Typing edits a local draft (snappy, no PUT
 * per keystroke) and commits the whole `blocks` array to the parent on blur; structural edits (add /
 * remove / reorder / kind / table rows+cols) commit immediately. The parent persists via a full-record
 * PUT, matching every other field on the card.
 */
function ProseBlocksEditor(props: {
  blocks: ProseBlock[];
  disabled: boolean;
  campaignId: number;
  onChange: (next: ProseBlock[]) => void;
}): JSX.Element {
  const { blocks: initial, disabled, campaignId, onChange } = props;
  const [draft, setDraft] = React.useState<ProseBlock[]>(initial);

  // Re-sync when the server row's blocks change identity (post-save round-trip / switching sections).
  const initialKey = JSON.stringify(initial);
  React.useEffect(() => {
    setDraft(asProseBlocks(JSON.parse(initialKey)));
  }, [initialKey]);

  // Live, local-only edit (typing) — no persistence until blur.
  const edit = (i: number, patch: Partial<ProseBlock>) =>
    setDraft((d) => d.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  // Persist the current draft (blur), or a structural change immediately.
  const commit = (next?: ProseBlock[]) => {
    const value = next ?? draft;
    if (next) setDraft(next);
    onChange(value);
  };

  const addBlock = () => commit([...draft, { kind: 'p', text: '' }]);
  const removeBlock = (i: number) => commit(draft.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= draft.length) return;
    const next = draft.slice();
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };
  const changeKind = (i: number, kind: ProseBlockKind) => {
    const next = draft.map((b, idx) => {
      if (idx !== i) return b;
      // Seed a 1×2 grid when a block becomes a table so there is something to edit.
      if (kind === 'table') return { ...b, kind, rows: b.rows ?? [['', '']] };
      const { rows, ...rest } = b;
      return { ...rest, kind };
    });
    commit(next);
  };

  // Table cell/row/column helpers operate on one block and commit immediately.
  const withRows = (i: number, rows: string[][]) =>
    commit(draft.map((b, idx) => (idx === i ? { ...b, rows } : b)));
  const editCell = (i: number, r: number, c: number, val: string) => {
    const rows = (draft[i].rows ?? []).map((row) => row.slice());
    if (!rows[r]) rows[r] = [];
    rows[r][c] = val;
    // Typing in a cell is local until blur, like the text fields above.
    setDraft((d) => d.map((b, idx) => (idx === i ? { ...b, rows } : b)));
  };
  const addRow = (i: number) => {
    const cols = draft[i].rows?.[0]?.length ?? 1;
    withRows(i, [...(draft[i].rows ?? []), Array(cols).fill('')]);
  };
  const removeRow = (i: number, r: number) =>
    withRows(i, (draft[i].rows ?? []).filter((_, idx) => idx !== r));
  const addCol = (i: number) => withRows(i, (draft[i].rows ?? [[]]).map((row) => [...row, '']));
  const removeCol = (i: number) =>
    withRows(i, (draft[i].rows ?? []).map((row) => row.slice(0, Math.max(1, row.length - 1))));

  return (
    <Stack spacing={1.5}>
      {draft.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No prose yet. {disabled ? '' : 'Add a block to start this section’s body.'}
        </Typography>
      )}
      {draft.map((block, i) => (
        <Box
          key={i}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              select
              label="Block"
              size="small"
              value={block.kind}
              disabled={disabled}
              onChange={(e) => changeKind(i, e.target.value as ProseBlockKind)}
              sx={{ minWidth: 160 }}
            >
              {BLOCK_KIND_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <span style={{ marginLeft: 'auto' }} />
            {!disabled && (
              <>
                <GhostButton onClick={() => move(i, -1)} disabled={i === 0}>
                  ↑
                </GhostButton>
                <GhostButton onClick={() => move(i, 1)} disabled={i === draft.length - 1}>
                  ↓
                </GhostButton>
                <GhostButton onClick={() => removeBlock(i)}>Remove</GhostButton>
              </>
            )}
          </Stack>

          {block.kind === 'table' ? (
            <Stack spacing={1}>
              {(block.rows ?? []).map((row, r) => (
                <Stack key={r} direction="row" spacing={1} alignItems="center">
                  {row.map((cell, c) => (
                    <TextField
                      key={c}
                      size="small"
                      fullWidth
                      placeholder={r === 0 ? 'Header' : 'Cell'}
                      value={cell}
                      disabled={disabled}
                      onChange={(e) => editCell(i, r, c, e.target.value)}
                      onBlur={() => commit()}
                    />
                  ))}
                  {!disabled && (
                    <GhostButton onClick={() => removeRow(i, r)} disabled={(block.rows ?? []).length <= 1}>
                      ✕ row
                    </GhostButton>
                  )}
                </Stack>
              ))}
              {!disabled && (
                <Stack direction="row" spacing={1}>
                  <GhostButton onClick={() => addRow(i)}>+ Row</GhostButton>
                  <GhostButton onClick={() => addCol(i)}>+ Column</GhostButton>
                  <GhostButton
                    onClick={() => removeCol(i)}
                    disabled={(block.rows?.[0]?.length ?? 0) <= 1}
                  >
                    ✕ column
                  </GhostButton>
                </Stack>
              )}
            </Stack>
          ) : (
            <Stack spacing={1}>
              <TokenTextField
                label="Lead (optional bold label)"
                value={block.lead ?? ''}
                disabled={disabled}
                campaignId={campaignId}
                minRows={1}
                onChange={(next) => edit(i, { lead: next })}
                onBlur={() => commit()}
              />
              <TokenTextField
                label="Text"
                value={block.text ?? ''}
                disabled={disabled}
                campaignId={campaignId}
                minRows={block.kind.startsWith('h') ? 1 : 2}
                onChange={(next) => edit(i, { text: next })}
                onBlur={() => commit()}
              />
            </Stack>
          )}
        </Box>
      ))}
      {!disabled && <GhostButton onClick={addBlock}>+ Add block</GhostButton>}
    </Stack>
  );
}

/**
 * The admin default for a section's preloaded content — the body shown when AI auto-generation is off
 * for a delivery (see `CampaignReportSection.preloaded_content`). Typing edits a local draft (no PUT per
 * keystroke) and commits the whole string on blur, matching every other field's persistence. Supports
 * the same `[Bracket]`/`{{group.key}}` tokens as authored prose. An SE can override this per delivery
 * from the report editor; this is the starting point they see.
 */
function PreloadedContentField(props: {
  value: string;
  disabled: boolean;
  campaignId: number;
  onCommit: (next: string) => void;
}): JSX.Element {
  const { value, disabled, campaignId, onCommit } = props;
  const [draft, setDraft] = React.useState(value);
  // Re-sync when the server row's value changes identity (post-save round-trip / switching sections).
  React.useEffect(() => {
    setDraft(value);
  }, [value]);
  return (
    <TokenTextField
      label="Preloaded content (used when AI generation is off)"
      value={draft}
      disabled={disabled}
      campaignId={campaignId}
      minRows={3}
      onChange={setDraft}
      onBlur={() => draft !== value && onCommit(draft)}
    />
  );
}

/**
 * A blank row shape for "Add section". The server's `section_id`/`title` are required non-blank
 * fields, and this is POSTed immediately (not held as a local draft), so it must carry real
 * placeholder values rather than empty strings — an empty create 400s ("This field may not be
 * blank.") before the row ever reaches the screen for the author to fill in. The placeholder id is
 * timestamp-suffixed so adding two new sections back-to-back (before either is renamed) can't collide.
 */
function blankSection(campaignId: number, order: number): Partial<CampaignReportSection> {
  return {
    campaign: campaignId,
    section_id: `new-section-${Date.now()}`,
    title: 'New section',
    number: String(order + 1),
    order,
    source: 'template',
    is_data_section: false,
    hidden_by_default: false,
    ai_question: null,
    ai_enabled: true,
    preloaded_content: '',
    blocks: [],
    metadata: {},
  };
}

/**
 * Prop-driven, campaign-scoped report-template editor for embedding (e.g. inside a content step's
 * Report template panel). The campaign is fixed by prop and the active view (Sections / Layout) lives
 * in internal state rather than a URL param — so there is no campaign picker and no URL coupling. There
 * is no campaign to go "back" to in the embedded case, so the section editor's `onBack` is a no-op.
 *
 * Only two views: **Sections** (each section's id/title/source/prompt/prose) and **Layout** (the visual
 * arrangement — the section grid AND the figures that fill it, combined on one screen because they are
 * two halves of the same "where does everything sit" question).
 */
export function ReportTemplateWorkspace({
  campaignId,
  canWrite,
}: {
  campaignId: number;
  canWrite: boolean;
}): JSX.Element {
  const [view, setView] = React.useState<'sections' | 'layout'>('sections');

  if (view === 'layout') {
    return <LayoutView campaignId={campaignId} canWrite={canWrite} onBack={() => setView('sections')} />;
  }
  return (
    <SectionEditor
      campaignId={campaignId}
      canWrite={canWrite}
      onBack={() => {}}
      onOpenLayout={() => setView('layout')}
    />
  );
}

/**
 * The combined visual-arrangement view: the section grid (drag/resize sections) stacked above the
 * figure editor (add/place charts and images), under one header and one back button. Previously these
 * were two separate destinations ("Layout" and "Canvas") whose functionality overlapped — the grid
 * already showed each section's figures — so they are condensed here into a single Layout screen.
 */
function LayoutView(props: {
  campaignId: number;
  canWrite: boolean;
  onBack: () => void;
}): JSX.Element {
  const { data: campaign } = useFetchCampaigns();
  const campaignName =
    ((campaign ?? []) as Campaign[]).find((c) => c.id === props.campaignId)?.name ??
    `Campaign ${props.campaignId}`;
  return (
    <BuilderRoot>
      <SectionHeader
        title={`Report Layout — ${campaignName}`}
        subtitle="Arrange the report's sections on the grid, then add and place the charts and images that fill them. Both are authored once here and render read-only for every SE."
        actions={<GhostButton onClick={props.onBack}>← Sections</GhostButton>}
      />
      <Box sx={{ mb: 2 }}>
        <TokenLegend />
      </Box>
      <SectionGridCanvas
        campaignId={props.campaignId}
        campaignName={campaignName}
        canWrite={props.canWrite}
        embedded
      />
      <Box sx={{ mt: 4 }}>
        <ReportCanvas
          campaignId={props.campaignId}
          campaignName={campaignName}
          canWrite={props.canWrite}
          embedded
        />
      </Box>
    </BuilderRoot>
  );
}

/** Grid of campaigns to pick which one's report template to edit. */
/** Editor for one campaign's ordered report-section rows. */
function SectionEditor(props: {
  campaignId: number;
  canWrite: boolean;
  onBack: () => void;
  onOpenLayout: () => void;
}): JSX.Element {
  const { campaignId, canWrite, onBack, onOpenLayout } = props;
  const { data: sectionRows, isLoading } = useReportSectionConfig(campaignId);
  const { data: questionRows } = useFetchAiQuestions();
  const { data: campaign } = useFetchCampaigns();
  const { create, update, remove, isSaving, error } = useCampaignMutations('reportSection');

  const questions = (questionRows ?? []) as { id: number; title: string; question: string }[];
  const campaignName =
    ((campaign ?? []) as Campaign[]).find((c) => c.id === campaignId)?.name ?? `Campaign ${campaignId}`;

  // Server rows, kept in `order` so reorder and the runner agree.
  const rows = React.useMemo(
    () => ((sectionRows ?? []) as CampaignReportSection[]).slice().sort((a, b) => a.order - b.order),
    [sectionRows],
  );

  // Full-record PUT (Go runtime 400s on a delta): merge the change onto the whole row.
  const saveField = React.useCallback(
    (row: CampaignReportSection, patch: Partial<CampaignReportSection>) => {
      update.mutate({ ...row, ...patch });
    },
    [update],
  );

  // Reorder swaps `order` with the neighbour and PUTs both whole rows.
  const move = React.useCallback(
    (index: number, dir: -1 | 1) => {
      const a = rows[index];
      const b = rows[index + dir];
      if (!a || !b) return;
      update.mutate({ ...a, order: b.order });
      update.mutate({ ...b, order: a.order });
    },
    [rows, update],
  );

  const addSection = React.useCallback(() => {
    const nextOrder = rows.length ? Math.max(...rows.map((r) => r.order)) + 1 : 0;
    create.mutate(blankSection(campaignId, nextOrder));
  }, [rows, campaignId, create]);

  return (
    <BuilderRoot>
      <SectionHeader
        title={`Report Builder — ${campaignName}`}
        subtitle="Reorder, rename, mark the data section, and bind each AI-written section to the stored prompt that generates it. Changes are read by the runner immediately."
        actions={
          <Stack direction="row" spacing={1}>
            <GhostButton onClick={onBack}>← All campaigns</GhostButton>
            <GhostButton onClick={onOpenLayout}>Layout &amp; figures →</GhostButton>
            {canWrite && (
              <PrimaryButton onClick={addSection} disabled={isSaving}>
                + Add section
              </PrimaryButton>
            )}
          </Stack>
        }
      />

      <Box sx={{ mb: 2 }}>
        <TokenLegend />
      </Box>

      <Alert severity="info" icon={false} sx={{ mb: 2 }}>
        The &ldquo;Before sharing&rdquo; banner shown above the report editor isn&rsquo;t configured
        here — it&rsquo;s a setting on the step that renders this report. Open the step in{' '}
        <strong>Steps</strong> (the campaign canvas), scroll its content template to{' '}
        <strong>Layout primitives</strong>, open the <strong>Report editor</strong> entry, and edit{' '}
        <strong>Example-content notice</strong>. Leave it blank to use the default wording.
      </Alert>

      {error != null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error)?.message ?? 'Save failed.'}
        </Alert>
      )}

      {isLoading && <p className={builderStyles.lead}>Loading sections…</p>}
      {!isLoading && rows.length === 0 && (
        <p className={builderStyles.lead}>
          This campaign has no report template yet. {canWrite ? 'Add a section to start one.' : ''}
        </p>
      )}

      <Stack spacing={2}>
        {rows.map((row, index) => (
          <Card key={row.id} className={builderStyles.contentCard}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <span className={builderStyles.tag}>{row.section_id || 'new-section'}</span>
              {row.is_data_section && <StatusPill tone="progress">Data section</StatusPill>}
              {row.ai_question != null && <StatusPill tone="done">AI-written</StatusPill>}
              {row.ai_question != null && row.ai_enabled === false && (
                <StatusPill tone="idle">AI off by default</StatusPill>
              )}
              {row.hidden_by_default && <StatusPill tone="idle">Hidden by default</StatusPill>}
              <span style={{ marginLeft: 'auto' }} />
              {canWrite && (
                <>
                  <GhostButton onClick={() => move(index, -1)} disabled={index === 0 || isSaving}>
                    ↑
                  </GhostButton>
                  <GhostButton
                    onClick={() => move(index, 1)}
                    disabled={index === rows.length - 1 || isSaving}
                  >
                    ↓
                  </GhostButton>
                  <GhostButton onClick={() => remove.mutate(row.id)} disabled={isSaving}>
                    Delete
                  </GhostButton>
                </>
              )}
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Autocomplete
                freeSolo
                size="small"
                options={KNOWN_SECTION_IDS}
                defaultValue={row.section_id}
                disabled={!canWrite}
                onChange={(_e, v) => {
                  const val = (v ?? '') as string;
                  if (val !== row.section_id) saveField(row, { section_id: val });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Section id"
                    helperText="Standard ids listed; must match the report engine's section id"
                    onBlur={(e) =>
                      e.target.value !== row.section_id &&
                      saveField(row, { section_id: e.target.value })
                    }
                  />
                )}
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Number"
                size="small"
                defaultValue={row.number}
                disabled={!canWrite}
                onBlur={(e) => e.target.value !== row.number && saveField(row, { number: e.target.value })}
                sx={{ width: 100 }}
              />
              <TextField
                label="Title"
                size="small"
                fullWidth
                defaultValue={row.title}
                disabled={!canWrite}
                onBlur={(e) => e.target.value !== row.title && saveField(row, { title: e.target.value })}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="center">
              <TextField
                select
                label="Source"
                size="small"
                value={row.source}
                disabled={!canWrite}
                onChange={(e) => saveField(row, { source: e.target.value as ReportSectionSource })}
                sx={{ minWidth: 200 }}
              >
                {SOURCE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="AI prompt (binds a stored ai_question)"
                size="small"
                value={row.ai_question ?? ''}
                disabled={!canWrite}
                onChange={(e) =>
                  saveField(row, {
                    ai_question: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                sx={{ minWidth: 280, flex: 1 }}
              >
                <MenuItem value="">
                  <em>None (template section)</em>
                </MenuItem>
                {questions.map((q) => (
                  <MenuItem key={q.id} value={q.id}>
                    {q.title}
                  </MenuItem>
                ))}
              </TextField>

              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Switch
                  checked={row.is_data_section}
                  disabled={!canWrite}
                  onChange={(e) => saveField(row, { is_data_section: e.target.checked })}
                />
                Data section
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Switch
                  checked={row.hidden_by_default}
                  disabled={!canWrite}
                  onChange={(e) => saveField(row, { hidden_by_default: e.target.checked })}
                />
                Hidden by default
              </label>
              {row.ai_question != null && (
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  title="When off, the report does not auto-generate this section on load; it shows the preloaded content below until the SE chooses Rewrite with AI."
                >
                  <Switch
                    checked={row.ai_enabled !== false}
                    disabled={!canWrite}
                    onChange={(e) => saveField(row, { ai_enabled: e.target.checked })}
                  />
                  Generate with AI on load
                </label>
              )}
            </Stack>

            {row.ai_question_prompt && (
              <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                <strong>Bound prompt:</strong> {row.ai_question_prompt}
              </Alert>
            )}

            {row.ai_question != null && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Preloaded content
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Stands in for the AI text when “Generate with AI on load” is off — either as the admin
                  default here, or an SE’s per-delivery override. An SE can still choose “Rewrite with AI”
                  to regenerate it.
                </Typography>
                <PreloadedContentField
                  value={row.preloaded_content ?? ''}
                  disabled={!canWrite}
                  campaignId={campaignId}
                  onCommit={(next) => saveField(row, { preloaded_content: next })}
                />
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Section prose
              </Typography>
              {row.source === 'generated' ? (
                <Alert severity="info" icon={false}>
                  This section is written at report time — its bound AI prompt, or the deterministic
                  draft. Switch Source to “Template prose” to author a fixed body here.
                </Alert>
              ) : (
                <>
                  {row.is_data_section && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      This is the data section — the workshop’s captured findings are drafted into it
                      at report time, alongside any prose you author here.
                    </Typography>
                  )}
                  <ProseBlocksEditor
                    blocks={asProseBlocks(row.blocks)}
                    disabled={!canWrite}
                    campaignId={campaignId}
                    onChange={(next) => saveField(row, { blocks: next })}
                  />
                </>
              )}
            </Box>
          </Card>
        ))}
      </Stack>
    </BuilderRoot>
  );
}
